import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ImpersonationService } from '@mysrc/app/modules/fwcore/services/impersonation.service';
import { OktaWrapperService } from '@mysrc/services/framework/okta-wrapper.service';
import { Observable } from 'rxjs';

import { BaseHttpReqRespService } from '../../../modules/fwcore/services';
import {
  RequestHelpCategory, RequestHelpCoverage, RequestHelpGlossaryScopeSet, RequestHelpItem,
  RequestHelpItemDelete, RequestHelpItemSet, RequestHelpItemSort, RequestHelpTree,
  ResponseHelpCategory, ResponseHelpCoverage, ResponseHelpGlossaryScopeSet,
  ResponseHelpItemAdmin, ResponseHelpItemDelete, ResponseHelpItemSet, ResponseHelpItemSort,
  ResponseHelpTree
} from '../models';

/**
 * HTTP access to the Help administration endpoints.
 *
 * A SEPARATE service from HelpApiService, mirroring the split on the server. The
 * reader client cannot reach an admin endpoint and the admin client is not used by
 * any reader screen, so the two projections stay apart on both sides of the wire.
 *
 * Every call here is refused with 403 unless the caller holds the Help administration
 * page grant. That is enforced server-side on every request - the Angular route guard
 * stops navigation but does nothing about a direct call - so a 403 from these methods
 * is a normal outcome to handle, not an impossible one.
 */
@Injectable({
  providedIn: 'root'
})
export class HelpAdminApiService extends BaseHttpReqRespService {

  // ----------------- Constants ---------------------------------------------

  readonly endPointHelpAdminTree: string = 'helpadmin/tree';
  readonly endPointHelpAdminItem: string = 'helpadmin/item';
  readonly endPointHelpAdminSort: string = 'helpadmin/item/sort';
  readonly endPointHelpAdminCategories: string = 'helpadmin/categories';
  readonly endPointHelpAdminCoverage: string = 'helpadmin/coverage';
  readonly endPointHelpAdminGlossaryScope: string = 'helpadmin/item/glossary-scope';

  // ----------------- Constructor -------------------------------------------

  constructor(
    http: HttpClient,
    @Inject('CONFIG_SETTINGS') config: any,
    oktaWrapper: OktaWrapperService,
    impersonation: ImpersonationService
  ) {
    super(http, config, impersonation, oktaWrapper);
  }

  // ----------------- Reads --------------------------------------------------

  /**
   * Gets the whole Help hierarchy, including items an administrator has parked.
   *
   * The server forces includeInactive on for this endpoint, so parked content is
   * always present - an administrator cannot manage what the API will not show them.
   */
  public helpAdminTreeGet$(req: RequestHelpTree): Observable<ResponseHelpTree> {

    return this.getData$<RequestHelpTree, ResponseHelpTree>(req, this.endPointHelpAdminTree);
  }

  /**
   * Gets Help items with authorship, the visibility flag and the context hints.
   *
   * Either ids or parentId is required; the server rejects a request with neither
   * rather than returning every article and its authorship.
   */
  public helpAdminItemGet$(req: RequestHelpItem): Observable<ResponseHelpItemAdmin> {

    return this.getData$<RequestHelpItem, ResponseHelpItemAdmin>(req, this.endPointHelpAdminItem);
  }

  /**
   * Gets the Help content categories for the editor's picker.
   *
   * An empty list is a normal response, not a failure: dbo.pb_HelpCategory can hold no
   * rows. Disable the picker in that case rather than reporting an error - offering a
   * code that is not in the table would fail the insert on save.
   */
  public helpAdminCategoryGet$(req: RequestHelpCategory): Observable<ResponseHelpCategory> {

    return this.getData$<RequestHelpCategory, ResponseHelpCategory>(req, this.endPointHelpAdminCategories);
  }

  /**
   * Gets the Help coverage gap report.
   *
   * Read from dbo.vw_pb_HelpCoverage server-side. Display the rows as given - group and
   * relabel them for reading, but do not filter or recompute them. The view is the
   * single definition of what counts as a gap, and this call exists precisely so that
   * definition is not duplicated on the client.
   *
   * The rows arrive most-actionable-first: empty articles, then duplicate hints, then
   * unreachable items. That order is the procedure's, so leave it alone.
   */
  public helpAdminCoverageGet$(req: RequestHelpCoverage): Observable<ResponseHelpCoverage> {

    return this.getData$<RequestHelpCoverage, ResponseHelpCoverage>(req, this.endPointHelpAdminCoverage);
  }

  // ----------------- Writes -------------------------------------------------

  /**
   * Creates or updates Help items.
   *
   * Send back the concurrencyToken each item was issued with. A stale token is
   * refused with 409, which means another author saved in the meantime - reload and
   * reapply rather than retrying, because there is no version history to recover a
   * lost edit from.
   */
  public helpAdminItemSet$(req: RequestHelpItemSet): Observable<ResponseHelpItemSet> {

    // PUT rather than POST: the base HTTP client has no postData$ that sends a JSON
    // body, and the controller accepts either verb behind one implementation because
    // the procedure decides insert-versus-update from whether an id was supplied.
    return this.putData$<RequestHelpItemSet, ResponseHelpItemSet>(req, this.endPointHelpAdminItem);
  }

  /**
   * Reorders the children of one Help item.
   *
   * Sends the whole new sibling order at once, so the order is never briefly
   * inconsistent.
   */
  public helpAdminItemSort$(req: RequestHelpItemSort): Observable<ResponseHelpItemSort> {

    return this.putData$<RequestHelpItemSort, ResponseHelpItemSort>(req, this.endPointHelpAdminSort);
  }

  /**
   * Permanently deletes a Help item and everything beneath it.
   *
   * TWO-PHASE, and the caller owns the phases. Call with force false first: nothing
   * is deleted and the response reports how many descendants would go. Only call
   * again with force true once a person has seen that count and confirmed.
   *
   * Deletes cascade, are unlogged, and there is no recycle bin or history - recovery
   * is a database restore. Do not collapse the two calls into one.
   */
  public helpAdminItemDelete$(req: RequestHelpItemDelete): Observable<ResponseHelpItemDelete> {

    return this.deleteData$<RequestHelpItemDelete, ResponseHelpItemDelete>(req, this.endPointHelpAdminItem);
  }

  /**
   * Sets, changes or clears the glossary scope one Help item shows.
   *
   * SEND THE THREE IDS THAT IDENTIFY THE SCOPE - dealGroupId, dealTypeId and towerId
   * from the chosen GlossaryScope - never its three request... ids. Those are for
   * reading terms and are deliberately a different shape.
   *
   * LEAVE ALL THREE UNSET TO CLEAR the mapping. That is the only way to clear it.
   *
   * A scope that holds no glossary terms is refused with 400 carrying the reason,
   * rather than saved and left to show an empty tab. Surface that message: an author
   * needs to tell an empty scope from a failed save.
   */
  public helpAdminGlossaryScopeSet$(
    req: RequestHelpGlossaryScopeSet): Observable<ResponseHelpGlossaryScopeSet> {

    return this.putData$<RequestHelpGlossaryScopeSet, ResponseHelpGlossaryScopeSet>(
      req, this.endPointHelpAdminGlossaryScope);
  }
}
